import {Box, Button, DialogActions, DialogContent, Stack, Tab, Tabs, Typography} from '@mui/material'
import {IndicatorInput} from '../../../../proto/generated/indicator'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {SeriesOutput} from '../../chart.typings'
import SeriesGroupInputs from './seriesGroupInputs'
import SeriesGroupOutputs from './seriesGroupOutputs'
import {MULTIPANE_FULLSCREEN_MODE_ID} from '../../../../assets/constants'
import {Security, SecurityCategory_CategoryCode} from '../../../../proto/generated/security'
import DraggableDialogTitle from '../../../common/DraggableDialogTitle'
import CustomDialog from '../../../common/CustomDialog'
import {ExtraSmallButton} from "../../../custom";
import ClearIcon from "../../../../assets/icons/ClearIcon";

export type SeriesGroupConfigurationPopupProps = {
    id: string,
    viewKey: string,
    inputValues: Record<string, any>
    outputValues: Record<string, SeriesOutput>
    fields: IndicatorInput[];
    title: string,
    onInputsUpdate: (inputValues: Record<string, any>) => void,
    onOutputsUpdate: (outputValues: Record<string, SeriesOutput>) => void,
    onClose: () => void
    onSubmit: () => void
    security?: Security | undefined
}

const SeriesGroupConfigurationPopup: React.FC<SeriesGroupConfigurationPopupProps> = ({
                                                                                         title,
                                                                                         fields,
                                                                                         inputValues,
                                                                                         onClose,
                                                                                         onSubmit,
                                                                                         outputValues: outputConfig,
                                                                                         onInputsUpdate,
                                                                                         onOutputsUpdate,
                                                                                         security
                                                                                     }) => {
    const initialInputValues = useRef<Record<string, any>>(inputValues)
    const initialOutputValues = useRef<Record<string, SeriesOutput>>(outputConfig)
    const onInputChange = (title: string, value: any) => {
        onInputsUpdate(finalInputs({
            ...inputValues,
            [title]: value
        }))
    }

    const disableInput = useMemo(() => {
        return title === 'Open Interest Outlook' && ![SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX].includes(security?.categoryCode as any)
    }, [security?.categoryCode, title])

    const [selectedTab, setSelectedTab] = useState<string>(fields.length && !disableInput ? 'inputs' : 'styles')

    const onOutputChange = (key: string, seriesOutput: SeriesOutput) => {
        const newOutputConfig = {...outputConfig}

        newOutputConfig[key] = seriesOutput
        onOutputsUpdate({
            ...newOutputConfig
        })

    }

    const tabs = useMemo(() => {
        return <Tabs
            value={selectedTab}
            onChange={(_event: React.SyntheticEvent, newTab: string) => {
                setSelectedTab(newTab)
            }}
            sx={{
                width: '100%',
                overflowX: 'auto',
                display: 'flex'
            }}
        >
            {
                fields.length && !disableInput && <Tab label={'Inputs'} value={'inputs'}/>
            }
            {
                Object.keys(outputConfig).length && <Tab label={'Styles'} value={'styles'}/>
            }
        </Tabs>
    }, [selectedTab, fields, disableInput, outputConfig])

    const finalInputs = useCallback((values: Record<string, any>) => {
        const fValues: Record<string, any> = {}
        for (const key in values) {
            if (typeof values[key] == 'number' && isNaN(values[key])) {
                fValues[key] = '0'
            } else {
                fValues[key] = values[key]
            }
        }
        return fValues
    }, [])

    const handleCancel = useCallback(() => {
        switch (selectedTab) {
            case 'inputs': {
                onInputsUpdate(initialInputValues.current)
                break
            }
            case 'styles': {
                onOutputsUpdate(initialOutputValues.current)
                break
            }
        }
        onClose()
    }, [selectedTab, onClose, onInputsUpdate, onOutputsUpdate])

    const handleDefault = useCallback(() => {
        const defaultInputValues: Record<string, any> = {}
        fields.forEach((field) => {
            defaultInputValues[field.title] = field.defval || ''
        })
        onInputsUpdate(finalInputs(defaultInputValues))
    }, [fields, finalInputs])

    return <CustomDialog open onClose={onClose} maxWidth={'xs'} fullWidth
                         container={document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)}>
        <DraggableDialogTitle>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'h6'}>{title}</Typography>
                <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                    <ClearIcon/>
                </ExtraSmallButton>
            </Stack>
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 1}}>
                {tabs}
            </Box>
        </DraggableDialogTitle>
        <DialogContent sx={{width: '100%', padding: '16px 20px'}}>
            {
                (selectedTab === 'inputs') ? (
                    <SeriesGroupInputs fields={fields} inputValues={inputValues} onInputChange={onInputChange}/>
                ) : (selectedTab === 'styles') ? (
                    <SeriesGroupOutputs seriesOutputsConfig={outputConfig} onSeriesOutputChange={onOutputChange}/>
                ) : null
            }
        </DialogContent>
        <DialogActions sx={{borderTop: 1, borderColor: 'divider', justifyContent: 'space-between'}}>
            <Button variant="outlined" sx={{
                justifyContent: 'flex-start',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.12)'
            }}
                    onClick={handleDefault}
            >Default</Button>
            <Box sx={{display: 'flex', gap: 1}}>
                <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={() => onSubmit()}>Ok</Button>
            </Box>
        </DialogActions>
    </CustomDialog>
}


export default SeriesGroupConfigurationPopup